import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { DateCalendarForm, Input, SelectAutocompleteMultiple } from 'components';
import { getNestedError, Images } from 'utils';
import { useFormBuilder } from '../../formContext';
import {
    buildSessionDataColumns,
    buildSessionDataTrialColumns,
    getSessionDataCellValue,
    getSessionDataColumnHeader,
    getSessionDataEmptyMessage,
    getSessionDataTrialCellValue,
    getSessionDataTrialColumnHeader,
    isManualSessionDataColumn,
    SESSION_DATA_MANUAL_KEY,
    sessionDataColumnList,
    sessionDataManualInputEnums,
    sessionDataTrialColumnList,
} from '../../sessionDataTable.constants';

const COLUMN_SCOPES = {
    SUMMARY: 'summary',
    TRIAL: 'trial',
};

export const ChevronIcon = ({ open }) => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

export const PencilIcon = ({ color = 'currentColor' }) => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
    >
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
);

/**
 * One MANUAL cell on the fill surface, built from the same controlled inputs
 * the rest of the form uses. Every answer is stored display-ready: a dropdown
 * stores the option's name (its options are keyed by name for that), a date
 * the `YYYY-MM-DD` DateCalendarForm writes — the portal and the PDF show them
 * as-is / formatted, like every AUTO cell.
 */
const ManualCell = ({ col, control, errors, name, readOnly }) => {
    const errorClass = getNestedError(errors, name) ? 'error' : '';
    const rules = { required: col.required ? 'Required' : false };

    if (col.inputType === sessionDataManualInputEnums.DROPDOWN) {
        // An option added in the panel but never named has nothing to show.
        const options = (col.options || []).filter((opt) => opt.name?.trim()).map((opt) => ({ id: opt.name, name: opt.name }));
        return (
            <div className={`builder-select-autocomplete ${errorClass}`}>
                <SelectAutocompleteMultiple
                    name={name}
                    control={control}
                    rules={rules}
                    options={options}
                    readOnly={readOnly}
                    renderValue={(option) => option?.name}
                    variant="standard"
                    placeholder="..."
                />
            </div>
        );
    }

    if (col.inputType === sessionDataManualInputEnums.DATE) {
        return (
            <div className={`report-builder-input-box ${errorClass}`}>
                <DateCalendarForm name={name} control={control} rules={rules} readOnly={readOnly} noErrMessage={true} />
            </div>
        );
    }

    const isNumber = col.inputType === sessionDataManualInputEnums.NUMBER;
    return (
        <div className={`report-builder-input-box ${errorClass}`}>
            <Input
                name={name}
                control={control}
                rules={rules}
                readOnly={readOnly}
                variant="standard"
                placeholder="..."
                noErrorMessage={true}
                {...(isNumber ? { type: 'number' } : { multiline: true })}
            />
        </div>
    );
};

const RequiredMark = ({ col }) =>
    isManualSessionDataColumn(col) && col.required ? <span className="session-data-required-mark">*</span> : null;

/**
 * A read-only header cell. With `markManual`, a MANUAL column is marked (green,
 * pencil, `*` when required) — only where staff actually type into it; on a
 * signed note, preview or PDF it reads like any other column.
 */
const StaticHeaderCell = ({ col, label, markManual }) => {
    const isMarked = markManual && isManualSessionDataColumn(col);
    return (
        <th className={isMarked ? 'session-data-column-manual' : ''}>
            <div className="table-header-cell-inner">
                {isMarked && <PencilIcon />}
                <span className="session-data-column-label">
                    {label}
                    {isMarked && <RequiredMark col={col} />}
                </span>
            </div>
        </th>
    );
};

/**
 * Dropdown that re-points a column at another session-data field. Fields already
 * used by a sibling column are disabled — the same field twice in one table
 * would render two identical columns.
 *
 * Positioned `fixed` off the trigger's measured rect rather than absolutely
 * inside the `<th>`: the surrounding `.table-element-wrapper` sets
 * `overflow: hidden` to clip its rounded corners, which would otherwise cut the
 * dropdown off. Same approach as TableMultiSelect in tableElement.js.
 */
export const FieldPicker = ({ catalog, usedFields, currentField, position, onSelect, onClose }) => {
    const pickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    return (
        <div
            ref={pickerRef}
            className="session-data-field-picker"
            style={{ position: 'fixed', top: position.top, left: position.left, minWidth: Math.max(position.width, 180) }}
        >
            {catalog.map((option) => {
                const isTaken = option.field !== currentField && usedFields.includes(option.field);
                return (
                    <button
                        type="button"
                        key={option.field}
                        className={`session-data-field-picker-option ${option.field === currentField ? 'active' : ''} ${
                            isTaken ? 'disabled' : ''
                        }`}
                        disabled={isTaken}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect(option.field);
                            onClose();
                        }}
                    >
                        <span>{option.name}</span>
                        {option.field === currentField && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0C66E4" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

/**
 * Session Data Table.
 *
 * A fixed-schema, auto-populated table: each column binds to a field of the
 * appointment's collected session data (see sessionDataTable.constants.js), so
 * unlike the free-form TABLE attribute there are no editable cells, no cell
 * types and no merge/split. In the builder the author only chooses WHICH fields
 * appear, in what order, and whether the per-trial breakdown is nested under
 * each row. Rows themselves are resolved when a note is filled.
 *
 * `builderMode` is the only editing surface (the template builder). Every other
 * surface — note fill, preview, PDF — renders the configured headers read-only,
 * with `item.value` rows when present and placeholder rows when not. The one
 * exception is MANUAL columns on the note fill surface (`control` + `name`
 * passed): staff fill those per row, into `<name>.<row>.manual.<columnId>`.
 * `errors` is the form's root formState, the same source every other field on
 * the fill surface colours itself from (`getNestedError(errors, name)`).
 */
export const SessionDataTableElement = ({
    item,
    index,
    readOnly,
    builderMode = false,
    formList,
    setFormList,
    activeSection,
    control,
    errors,
    name,
}) => {
    const { setOnDragStart } = useFormBuilder();
    const [openPicker, setOpenPicker] = useState(null);
    const [trialsOpen, setTrialsOpen] = useState(true);

    const config = item?.properties?.sessionDataTable || {};
    const columns = useMemo(() => config.columns || [], [config.columns]);
    const trialColumns = useMemo(() => config.trialColumns || [], [config.trialColumns]);
    const includeTrialDetails = !!config.includeTrialDetails;
    const selectedColumnId = config._selectedColumnId || null;
    const isEditable = builderMode && !readOnly;
    // MANUAL columns take input only on the fill surface (`control`, not read-only).
    const markManual = !!control && !readOnly;

    const rows = Array.isArray(item?.value) ? item.value : [];

    /**
     * Copy-on-write persist of the block config back into the template. Mirrors
     * TableElement's context path: address the attribute by the active section +
     * attribute index and replace `properties.sessionDataTable` wholesale.
     */
    const updateConfig = useCallback(
        (patch) => {
            if (!isEditable || !setFormList) return;
            const sectionIndex = (activeSection || 1) - 1;
            setFormList((prev) => {
                const section = prev?.sections?.[sectionIndex];
                if (!section?.attributes?.[index]) return prev;
                const attributes = [...section.attributes];
                const current = attributes[index];
                attributes[index] = {
                    ...current,
                    properties: {
                        ...(current.properties || {}),
                        sessionDataTable: {
                            ...(current.properties?.sessionDataTable || {}),
                            ...patch,
                        },
                    },
                };
                const sections = [...prev.sections];
                sections[sectionIndex] = { ...section, attributes };
                return { ...prev, sections };
            });
        },
        [isEditable, setFormList, activeSection, index]
    );

    // Stable identity so FieldPicker's click-outside effect isn't torn down and
    // re-registered on every parent render.
    const closePicker = useCallback(() => setOpenPicker(null), []);

    const selectColumn = (columnId, scope) => {
        if (!isEditable) return;
        updateConfig({ _selectedColumnId: columnId, _selectedColumnScope: scope });
    };

    const addColumn = (scope) => {
        const isTrial = scope === COLUMN_SCOPES.TRIAL;
        const catalog = isTrial ? sessionDataTrialColumnList : sessionDataColumnList;
        const existing = isTrial ? trialColumns : columns;
        const nextField = catalog.find((option) => !existing.some((col) => col.field === option.field))?.field;
        // Every catalog field is already on the table — nothing left to add.
        if (!nextField) return;
        const [newColumn] = isTrial ? buildSessionDataTrialColumns([nextField]) : buildSessionDataColumns([nextField]);
        updateConfig({
            [isTrial ? 'trialColumns' : 'columns']: [...existing, newColumn],
            _selectedColumnId: newColumn.id,
            _selectedColumnScope: scope,
        });
    };

    const removeColumn = (columnId, scope) => {
        const isTrial = scope === COLUMN_SCOPES.TRIAL;
        const existing = isTrial ? trialColumns : columns;
        // A table with no columns has nothing to render — keep the last one.
        if (existing.length <= 1) return;
        updateConfig({
            [isTrial ? 'trialColumns' : 'columns']: existing.filter((col) => col.id !== columnId),
            ...(selectedColumnId === columnId ? { _selectedColumnId: null, _selectedColumnScope: null } : {}),
        });
    };

    const setColumnField = (columnId, field, scope) => {
        const isTrial = scope === COLUMN_SCOPES.TRIAL;
        const existing = isTrial ? trialColumns : columns;
        updateConfig({
            [isTrial ? 'trialColumns' : 'columns']: existing.map((col) => (col.id === columnId ? { ...col, field } : col)),
        });
    };

    const handleDragEnd = (result) => {
        setOnDragStart?.(false);
        if (!result?.destination || result.destination.index === result.source.index) return;
        const scope = result.source.droppableId.startsWith('session-data-trial') ? COLUMN_SCOPES.TRIAL : COLUMN_SCOPES.SUMMARY;
        const isTrial = scope === COLUMN_SCOPES.TRIAL;
        const reordered = [...(isTrial ? trialColumns : columns)];
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        updateConfig({ [isTrial ? 'trialColumns' : 'columns']: reordered });
    };

    const columnHeaderLabel = (col, scope) =>
        scope === COLUMN_SCOPES.TRIAL ? getSessionDataTrialColumnHeader(col, col.dataType) : getSessionDataColumnHeader(col);

    /**
     * A body cell. On the fill surface a MANUAL column is an input bound to
     * `<recordPath>.manual.<columnId>` — the summary row or one of its trials;
     * everywhere else it shows the stored answer.
     */
    const renderValueCell = (col, recordPath, display) =>
        control && isManualSessionDataColumn(col) ? (
            <ManualCell
                col={col}
                control={control}
                errors={errors}
                name={`${recordPath}.${SESSION_DATA_MANUAL_KEY}.${col.id}`}
                readOnly={readOnly}
            />
        ) : (
            <span>{display}</span>
        );

    const renderHeaderCells = (cols, scope) => {
        const catalog = scope === COLUMN_SCOPES.TRIAL ? sessionDataTrialColumnList : sessionDataColumnList;
        const usedFields = cols.map((col) => col.field);

        return cols.map((col, colIndex) => {
            const label = columnHeaderLabel(col, scope);
            const isManual = isManualSessionDataColumn(col);
            const isSelected = isEditable && selectedColumnId === col.id;
            const pickerKey = `${scope}-${col.id}`;
            const manualClass = isManual ? 'session-data-column-manual' : '';
            const requiredMark = <RequiredMark col={col} />;

            if (!isEditable) return <StaticHeaderCell key={col.id} col={col} label={label} markManual={markManual} />;

            return (
                <Draggable key={col.id} draggableId={pickerKey} index={colIndex}>
                    {(dragProvided) => (
                        <th
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`${isSelected ? 'table-cell-selected' : ''} ${manualClass}`}
                            onClick={() => selectColumn(col.id, scope)}
                            style={{ ...dragProvided.draggableProps.style }}
                        >
                            <div className="table-header-cell-inner">
                                <div {...dragProvided.dragHandleProps} className="table-col-drag-handle">
                                    <img src={Images.dragIcon} alt="" className="table-drag-icon" />
                                </div>
                                {isManual && <PencilIcon />}
                                <span className="session-data-column-label">
                                    {label}
                                    {requiredMark}
                                </span>
                                {!isManual && (
                                    <button
                                        type="button"
                                        className="session-data-column-chevron"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            selectColumn(col.id, scope);
                                            if (openPicker?.key === pickerKey) {
                                                setOpenPicker(null);
                                                return;
                                            }
                                            const rect = e.currentTarget.closest('th').getBoundingClientRect();
                                            setOpenPicker({
                                                key: pickerKey,
                                                position: { top: rect.bottom + 4, left: rect.left, width: rect.width },
                                            });
                                        }}
                                    >
                                        <ChevronIcon open={openPicker?.key === pickerKey} />
                                    </button>
                                )}
                                {cols.length > 1 && (
                                    <button
                                        type="button"
                                        className="session-data-column-remove"
                                        title="Remove column"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeColumn(col.id, scope);
                                        }}
                                    >
                                        <img src={Images.trash} alt="remove column" />
                                    </button>
                                )}
                            </div>
                            {openPicker?.key === pickerKey && (
                                <FieldPicker
                                    catalog={catalog}
                                    usedFields={usedFields}
                                    currentField={col.field}
                                    position={openPicker.position}
                                    onSelect={(field) => setColumnField(col.id, field, scope)}
                                    onClose={closePicker}
                                />
                            )}
                        </th>
                    )}
                </Draggable>
            );
        });
    };

    /**
     * Per-trial breakdown for ONE data row, rendered immediately beneath it as a
     * full-width nested table rather than as a separate panel — a trial only
     * means anything next to the goal/target it belongs to.
     */
    const renderTrialDetails = (row, rowIndex, summaryColCount) => (
        <tr className="session-data-trial-row">
            <td colSpan={summaryColCount}>
                <div className="session-data-trial-panel">
                    <div className="table-element-wrapper">
                        <table className="table-element-box">
                            <thead>
                                <tr className="table-element-head-wrapper">
                                    {trialColumns.map((col) => (
                                        <StaticHeaderCell
                                            key={col.id}
                                            col={col}
                                            label={getSessionDataTrialColumnHeader(col, row?.dataType)}
                                            markManual={markManual}
                                        />
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {row.trials.map((trial, trialIndex) => (
                                    <tr key={trial?.id || trialIndex} className="table-element-body-wrapper">
                                        {trialColumns.map((col) => (
                                            <td key={col.id}>
                                                {renderValueCell(
                                                    col,
                                                    `${name}.${rowIndex}.trials.${trialIndex}`,
                                                    getSessionDataTrialCellValue(col, trial)
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </td>
        </tr>
    );

    /**
     * Body rows. In the builder there is no appointment to read from, so a
     * single "Empty" row stands in for the resolved data. On a filled note the
     * resolved rows render; when there are none the block says WHY (session not
     * completed / nothing collected) instead of showing blank placeholder rows
     * that read as missing data.
     */
    const renderBodyRows = (cols, scope) => {
        if (isEditable) {
            return (
                <tr className="table-element-body-wrapper">
                    <td className="session-data-drag-spacer" />
                    {cols.map((col) => (
                        <td key={col.id}>
                            <span className="session-data-empty-cell">
                                {isManualSessionDataColumn(col)
                                    ? 'Filled by staff'
                                    : scope === COLUMN_SCOPES.TRIAL
                                    ? 'Empty'
                                    : 'Auto-filled'}
                            </span>
                        </td>
                    ))}
                </tr>
            );
        }

        if (scope !== COLUMN_SCOPES.SUMMARY) return null;

        if (!rows.length) {
            return (
                <tr className="table-element-body-wrapper">
                    <td colSpan={cols.length}>
                        <span className="session-data-empty-cell">{getSessionDataEmptyMessage(item?.sessionDataState)}</span>
                    </td>
                </tr>
            );
        }

        return rows.map((row, rowIndex) => (
            <React.Fragment key={row?.id || rowIndex}>
                <tr className="table-element-body-wrapper">
                    {cols.map((col) => (
                        <td key={col.id}>{renderValueCell(col, `${name}.${rowIndex}`, getSessionDataCellValue(col, row))}</td>
                    ))}
                </tr>
                {includeTrialDetails && !!row?.trials?.length && renderTrialDetails(row, rowIndex, cols.length)}
            </React.Fragment>
        ));
    };

    const renderTable = (cols, scope, droppableId) => (
        <table className="table-element-box">
            <thead>
                {isEditable ? (
                    <Droppable droppableId={droppableId} direction="horizontal">
                        {(dropProvided) => (
                            <tr className="table-element-head-wrapper" ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                                <th className="table-drag-handle-col" />
                                {renderHeaderCells(cols, scope)}
                                {dropProvided.placeholder}
                            </tr>
                        )}
                    </Droppable>
                ) : (
                    <tr className="table-element-head-wrapper">{renderHeaderCells(cols, scope)}</tr>
                )}
            </thead>
            <tbody>{renderBodyRows(cols, scope)}</tbody>
        </table>
    );

    const body = (
        <>
            <div className="session-data-table-block">
                <div className="table-element-wrapper">{renderTable(columns, COLUMN_SCOPES.SUMMARY, `session-data-columns-${index}`)}</div>
                {isEditable && (
                    <button
                        style={{ marginTop: 8 }}
                        type="button"
                        className="table-add-column-btn"
                        title="Add column"
                        onClick={() => addColumn(COLUMN_SCOPES.SUMMARY)}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#0C66E4"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                        >
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Standalone trial panel is the BUILDER's config surface only: there is
                no appointment to read from there, so the author needs somewhere to
                pick the trial columns. On every other surface the breakdown is
                nested under the data row it belongs to (renderTrialDetails). */}
            {includeTrialDetails && isEditable && (
                <div className="session-data-table-block session-data-trial-block">
                    <div className="session-data-trial-panel">
                        <button type="button" className="session-data-trial-header" onClick={() => setTrialsOpen((prev) => !prev)}>
                            <ChevronIcon open={trialsOpen} />
                            <span>Trial details</span>
                        </button>
                        {trialsOpen && (
                            <div className="table-element-wrapper">
                                {renderTable(trialColumns, COLUMN_SCOPES.TRIAL, `session-data-trial-columns-${index}`)}
                            </div>
                        )}
                    </div>
                    {trialsOpen && (
                        <button
                            type="button"
                            className="table-add-column-btn"
                            title="Add trial column"
                            onClick={() => addColumn(COLUMN_SCOPES.TRIAL)}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#0C66E4"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                            >
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </button>
                    )}
                </div>
            )}
        </>
    );

    // Column drag lives inside the attribute's own Draggable — suppress the
    // parent block drag while the pointer is over this element, the same guard
    // uploadFileSignature uses for its inner drop zone.
    return (
        <div
            className="session-data-table-element"
            onMouseEnter={() => isEditable && setOnDragStart?.(true)}
            onMouseLeave={() => isEditable && setOnDragStart?.(false)}
        >
            {isEditable ? <DragDropContext onDragEnd={handleDragEnd}>{body}</DragDropContext> : body}
        </div>
    );
};
